import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Heart, LifeBuoy, LogIn, LogOut, User, LayoutDashboard } from "lucide-react";
import { useAuth } from "../lib/auth";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Business Services" },
    { to: "/workforce", label: "Workforce Initiative" },
    { to: "/about", label: "About & Capability" },
    { to: "/contact", label: "Contact" },
];

export default function Layout() {
    const [open, setOpen] = useState(false);
    const { user, logout, startGoogleSignIn } = useAuth();
    const { pathname } = useLocation();
    const isHome = pathname === "/";

    return (
        <div className="min-h-screen flex flex-col bg-[var(--brand-cream)]">
            <header
                className={`sticky top-0 z-50 backdrop-blur-md ${
                    isHome ? "bg-[#FFFDF8]/85" : "bg-[#FFFDF8]/95"
                } border-b border-slate-200/60`}
                data-testid="site-header"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
                            <img
                                src="/logo-color.png"
                                alt="Admin This! N Thatz Syncd, LLC"
                                className="h-12 w-12 rounded-full object-cover ring-2 ring-[var(--brand-gold)]/40"
                            />
                            <div className="hidden sm:block leading-tight">
                                <div className="font-serif text-[15px] font-medium text-[var(--brand-navy)]">
                                    Admin This! <span className="text-[var(--brand-coral)]">N Thatz</span> Syncd
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                                    Faith · Service · Empowerment
                                </div>
                            </div>
                        </Link>

                        <nav className="hidden lg:flex items-center gap-1">
                            {NAV.map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    end={n.to === "/"}
                                    className={({ isActive }) =>
                                        `px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                                            isActive
                                                ? "text-[var(--brand-navy)] bg-[var(--brand-gold)]/20"
                                                : "text-slate-600 hover:text-[var(--brand-navy)] hover:bg-slate-100"
                                        }`
                                    }
                                    data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
                                >
                                    {n.label}
                                </NavLink>
                            ))}
                        </nav>

                        <div className="flex items-center gap-2">
                            <Link to="/workforce#get-help" className="hidden md:inline-flex btn-outline-navy !text-[var(--brand-navy)] !border-[var(--brand-navy)] !px-5 !py-2 text-sm" data-testid="header-need-help-btn">
                                <LifeBuoy className="w-4 h-4 mr-1.5" /> Need Help?
                            </Link>
                            <Link to="/workforce" className="hidden md:inline-flex btn-gold !px-5 !py-2 text-sm" data-testid="header-donate-btn">
                                <Heart className="w-4 h-4 mr-1.5" /> Donate
                            </Link>
                            {user ? (
                                <UserMenu user={user} onLogout={logout} />
                            ) : (
                                <button
                                    onClick={() => startGoogleSignIn("/account")}
                                    className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-slate-700 hover:bg-slate-100 border border-slate-200 transition-colors"
                                    data-testid="header-signin-btn"
                                >
                                    <GoogleIcon /> Sign in
                                </button>
                            )}
                            <button
                                className="lg:hidden p-2"
                                onClick={() => setOpen((v) => !v)}
                                data-testid="header-mobile-menu-btn"
                                aria-label="Menu"
                            >
                                {open ? <X /> : <Menu />}
                            </button>
                        </div>
                    </div>

                    {open && (
                        <div className="lg:hidden pb-4 flex flex-col gap-1" data-testid="mobile-menu">
                            {NAV.map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    end={n.to === "/"}
                                    onClick={() => setOpen(false)}
                                    className={({ isActive }) =>
                                        `px-4 py-2.5 text-sm font-medium rounded-xl ${
                                            isActive
                                                ? "bg-[var(--brand-gold)]/20 text-[var(--brand-navy)]"
                                                : "text-slate-700 hover:bg-slate-100"
                                        }`
                                    }
                                >
                                    {n.label}
                                </NavLink>
                            ))}
                            <Link to="/workforce#get-help" onClick={() => setOpen(false)} className="btn-outline-navy mt-3 !px-5 !py-2 text-sm self-start">
                                <LifeBuoy className="w-4 h-4 mr-1.5" /> Need Help?
                            </Link>
                            <Link to="/workforce" onClick={() => setOpen(false)} className="btn-gold mt-2 self-start">
                                <Heart className="w-4 h-4 mr-2" /> Donate
                            </Link>
                            {user ? (
                                <>
                                    <Link to="/account" onClick={() => setOpen(false)} className="mt-2 text-sm font-medium text-slate-700 flex items-center gap-2 self-start px-2 py-1">
                                        <User className="w-4 h-4" /> My Account
                                    </Link>
                                    <button onClick={() => { setOpen(false); logout(); }} className="mt-1 text-sm text-slate-500 flex items-center gap-2 self-start px-2 py-1" data-testid="mobile-signout-btn">
                                        <LogOut className="w-4 h-4" /> Sign out
                                    </button>
                                </>
                            ) : (
                                <button onClick={() => { setOpen(false); startGoogleSignIn("/account"); }} className="mt-2 text-sm font-medium text-slate-700 flex items-center gap-2 self-start px-2 py-1" data-testid="mobile-signin-btn">
                                    <LogIn className="w-4 h-4" /> Sign in with Google
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </header>

            <main className="flex-1">
                <Outlet />
            </main>

            <Footer />
        </div>
    );
}

function Footer() {
    return (
        <footer className="relative night-wash text-white mt-24" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
                    <div className="md:col-span-2">
                        <div className="flex items-center gap-3 mb-5">
                            <img
                                src="/logo-inverse.png"
                                alt="TT Admins"
                                className="h-14 w-14 rounded-full object-cover"
                            />
                            <div>
                                <div className="font-serif text-xl">Admin This! N Thatz Syncd, LLC</div>
                                <div className="text-xs uppercase tracking-[0.2em] text-amber-200/70 font-semibold mt-1">
                                    Faith · Service · Empowerment
                                </div>
                            </div>
                        </div>
                        <p className="text-slate-300 max-w-md leading-relaxed">
                            An SBA-certified Woman-Owned & Veteran-Owned small business synchronizing administrative excellence, educational resources for families, and workforce restoration for our veterans.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            <span className="text-xs px-3 py-1.5 rounded-full bg-white/10 border border-white/15">SBA Certified WOSB</span>
                            <span className="text-xs px-3 py-1.5 rounded-full bg-white/10 border border-white/15">SBA Certified VOSB</span>
                            <span className="text-xs px-3 py-1.5 rounded-full bg-white/10 border border-white/15">UEI: MZYVNN8T4B71</span>
                            <span className="text-xs px-3 py-1.5 rounded-full bg-white/10 border border-white/15">CAGE: 0SQV5</span>
                        </div>
                    </div>

                    <div>
                        <h4 className="text-sm font-semibold uppercase tracking-wider text-amber-200/80 mb-4">Explore</h4>
                        <ul className="space-y-2 text-sm text-slate-300">
                            {NAV.map((n) => (
                                <li key={n.to}>
                                    <Link to={n.to} className="hover:text-white transition-colors">{n.label}</Link>
                                </li>
                            ))}
                            <li><Link to="/admin/login" className="hover:text-white transition-colors text-slate-400" data-testid="footer-admin-link">Admin Portal</Link></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-sm font-semibold uppercase tracking-wider text-amber-200/80 mb-4">Contact</h4>
                        <address className="not-italic text-sm text-slate-300 space-y-2 leading-relaxed">
                            <div>Christina Slimp, CEO</div>
                            <div>1920 Lakepointe Dr<br />Lewisville, TX 75057</div>
                            <a href="tel:+19729429022" className="block hover:text-white">(972) 942-9022</a>
                            <a href="mailto:cslimp@ttadmins.com" className="block hover:text-white">cslimp@ttadmins.com</a>
                            <a
                                href="/capability-statement.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center mt-3 text-amber-300 hover:text-amber-200 underline underline-offset-4"
                                data-testid="footer-capability-pdf"
                            >
                                Download Capability Statement
                            </a>
                            <a
                                href="/veteran-resources.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center mt-1 text-amber-300 hover:text-amber-200 underline underline-offset-4"
                                data-testid="footer-veteran-resources-pdf"
                            >
                                Veteran Resource One-Pager
                            </a>
                        </address>
                    </div>
                </div>

                <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-slate-400">
                    <div>© {new Date().getFullYear()} Admin This! N Thatz Syncd, LLC. All rights reserved.</div>
                    <div className="text-amber-200/70 text-xs uppercase tracking-[0.2em] font-semibold">Faith · Service · Empowerment</div>
                </div>
            </div>
        </footer>
    );
}


function GoogleIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
    );
}

function UserMenu({ user, onLogout }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    return (
        <div className="hidden md:block relative" ref={ref}>
            <button
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-2 px-2 py-1 rounded-full hover:bg-slate-100 transition-colors"
                data-testid="header-user-menu-btn"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full ring-2 ring-[var(--brand-gold)]/40" />
                ) : (
                    <div className="w-8 h-8 rounded-full bg-[var(--brand-navy)] text-white flex items-center justify-center text-sm font-semibold">
                        {user.name?.charAt(0).toUpperCase() || "U"}
                    </div>
                )}
                <span className="text-sm font-medium text-slate-700 max-w-[100px] truncate">{user.name?.split(" ")[0]}</span>
            </button>

            {open && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 z-50" data-testid="user-menu-dropdown">
                    <div className="px-4 py-3 border-b border-slate-100">
                        <div className="text-sm font-semibold text-[var(--brand-navy)] truncate">{user.name}</div>
                        <div className="text-xs text-slate-500 truncate">{user.email}</div>
                    </div>
                    <Link to="/account" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="menu-account-link">
                        <User className="w-4 h-4" /> My Account
                    </Link>
                    {user.role === "admin" && (
                        <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="menu-admin-link">
                            <LayoutDashboard className="w-4 h-4" /> Admin Console
                        </Link>
                    )}
                    <button onClick={() => { setOpen(false); onLogout(); }} className="w-full text-left flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100 mt-1" data-testid="menu-signout-btn">
                        <LogOut className="w-4 h-4" /> Sign out
                    </button>
                </div>
            )}
        </div>
    );
}
